import {Box, Button, Divider, Stack, Typography} from "@mui/material";
import {AddCircleOutline} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {ApiRequestFailed, authenticateAndFetchData, retryFn} from "@lib/apis";
import {useQuery} from "@tanstack/react-query";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {Link, useNavigate} from "react-router-dom";
import {urls} from "@routes";
import {CustomCellRendererProps} from "ag-grid-react";
import { format } from "date-fns";
import {useSnackbar} from "notistack";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { Downloading } from "@mui/icons-material";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface Plan {
	id: number
	name: string
	plan_tier: number
	created_on_ts: number
	monthly_amount: number
	annual_amount: number
	monthly_amount_inr: number
	annual_amount_inr: number
	popular: boolean
	hidden: boolean
	is_free_plan: boolean
	monthly_email_sending_quota: number
}

interface PaginationData {
	data: Plan[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
}


export default function AdminSubscriptionPlans() {
	const navigate = useNavigate();
	const { user } = useAuth();
	const {enqueueSnackbar} = useSnackbar();

	const [pageData, setPageData] = useState<PaginationData>();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [exportLoading, setExportLoading] = useState(false);

	// Query for fetching subscription plans data.
	const pageDataQuery = useQuery({
		queryKey: [
			"adminAllSubscriptionPlansPageData",
			page,
			pageSize,
			search
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/all-subscription-plans/?page=${page}&page_size=${pageSize}&search=${search}`
			),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (pageDataQuery.data?.data) {
			const response = pageDataQuery.data.data;

			setPageData({
				data: response.data || [],
				total_count: response.total_count,
				total_pages: response.total_pages,
				current_page: response.current_page,
				page_size: response.page_size,
			});
		}
	}, [pageDataQuery.data]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	function timestampFormatter(params: ValueFormatterParams) {
		if (params.value !== null) {
			return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
		} else {
			return "---"
		}
	}

	const EditButton = (props: CustomCellRendererProps) => {
		return (
			<Button component={Link} to={urls["adminEditSubscriptionPlan"].replace(":planId", props.data["id"])}>
				Edit
			</Button>
		)
	}

	const columnCustomizerLabel = {
		name: "Plan Name",
		created_on_ts: "Created On",
		plan_tier: "Plan Tier",
		monthly_amount: "Monthly Price ($)",
		annual_amount: "Annual Price ($)",
		monthly_amount_inr: "Monthly Price (₹)",
		annual_amount_inr: "Annual Price (₹)",
		popular: "Popular",
		hidden: "Hidden",
		is_free_plan: "Default Free Plan",
		monthly_email_sending_quota: "Monthly Email Quota",
	};

	// Columns for user table.
	const columnDefs: ColDef[] = [
		{field: "name", headerName: "Plan Name"},
		{field: "created_on_ts", headerName: "Created On", valueFormatter: timestampFormatter},
		{field: "plan_tier", headerName: "Plan Tier"},
		{field: "monthly_amount", headerName: "Monthly Price ($)"},
		{field: "annual_amount", headerName: "Annual Price ($)"},
		{field: "monthly_amount_inr", headerName: "Monthly Price (₹)"},
		{field: "annual_amount_inr", headerName: "Annual Price (₹)"},
		{field: "popular", headerName: "Popular"},
		{field: "hidden", headerName: "Hidden"},
		{field: "is_free_plan", headerName: "Default Free Plan"},
		{field: "monthly_email_sending_quota", headerName: "Monthly Email Quota"},
		{headerName: "", resizable: false, cellRenderer: EditButton},
	]

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	const PlanColumns: CSVColumn<Plan>[] = [
		{ header: "Plan Name", accessor: "name" },
		{ 
			header: "Created On", 
			accessor: (row) => 
				row.created_on_ts 
					? format(new Date(row.created_on_ts), "yyyy-MM-dd HH:mm:ss") 
					: "---" 
		},
		{ header: "Plan Tier", accessor: "plan_tier" },
		{ header: "Monthly Price ($)", accessor: "monthly_amount" },
		{ header: "Annual Price ($)", accessor: "annual_amount" },
		{ header: "Monthly Price (₹)", accessor: "monthly_amount_inr" },
		{ header: "Annual Price (₹)", accessor: "annual_amount_inr" },
		{ header: "Popular", accessor: (row) => (row.popular ? "True" : "False") },
		{ header: "Hidden", accessor: (row) => (row.hidden ? "True" : "False") },
		{ header: "Default Free Plan", accessor: (row) => (row.is_free_plan ? "True" : "False") },
		{ header: "Monthly Email Quota", accessor: "monthly_email_sending_quota" },
	];

	const handleExportPlans = async () => {
		setExportLoading(true);

		try {
			const response = await authenticateAndFetchData(
				`/admin/all-subscription-plans/?export_all=true`
			);
			
			const plansToExport = response?.data?.data || [];

			if (!plansToExport.length) {
				enqueueSnackbar("No data available to export.", { variant: "error" });
				return;
			}
		
			const success = exportToCSV(plansToExport, PlanColumns, getExportFilename(user?.email ?? "unknown", "SubscriptionPlans"));

			if (success) {
				enqueueSnackbar("Subscription plans exported!", { variant: "success" });
			} else {
				enqueueSnackbar("No data available to export.", { variant: "error" });
			}
		} catch (err) {
			console.error(err);
			enqueueSnackbar("Error while exporting subscription plans.", { variant: "error" });
		} finally {
			setExportLoading(false);
		}
	};

	const ExportPlansCsvButton = () => {
		return (
			<Button startIcon={<Downloading />}
				variant="contained"
				color="primary"
				onClick={() => {
					handleExportPlans();
				}}
				disabled={exportLoading}
			>
				{exportLoading ? "Exporting..." : "Export"}
			</Button>
		)
	}

	if (!pageData && pageDataQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
				<Typography fontWeight={"bold"} variant={"h5"}>Manage Subscription Plans</Typography>
				<Divider/>
				<Stack direction={"column"} spacing={2} sx={{height: "100%"}}>
					{/* Table Actions */}
					<Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
						<Stack direction={"row"} spacing={2}>
							<Button variant={"contained"}
											size={"small"}
											startIcon={<AddCircleOutline/>}
											disabled={false}
											onClick={() => {
												navigate(urls["adminCreateSubscriptionPlans"]);
											}}>
								Add New Plan
							</Button>
						</Stack>
					</Box>
					{/* Table */}
					<Box sx={{height: "100%"}}>
						<PaginatedServerSideDataTable
							columns={columnDefs}
							paginationData={pageData}
							loading={
								pageDataQuery.isLoading ||
								pageDataQuery.isFetching
							}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
							onSearchChange={handleSearchChange}
							searchPlaceholder="Search by plan name..."
							columnLabels={columnCustomizerLabel}
							actions={<ExportPlansCsvButton />}
						/>
					</Box>
				</Stack>
			</Stack>
		)

	} else {
		// Ideally it should not reach here.
		return <></>
	}
}
